import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import GeometryStatePill from "@/components/GeometryStatePill";
import { toast } from "sonner";
import { UploadCloud, XCircle } from "lucide-react";
import { fmtDate } from "@/lib/format";

const TERMINAL = new Set(["review_required", "verified", "unsupported", "failed", "cancelled"]);

export default function GeometryJobsPage() {
  const {session}=useAuth();
  const readOnly=session?.user?.role==='viewer';
  const [jobs, setJobs] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState({});
  const timerRef = useRef(null);

  async function refresh() {
    const [j, f] = await Promise.all([api.get("/geometry-jobs"), api.get("/files")]);
    setJobs(j.data);
    setFiles(f.data);
  }

  useEffect(() => {
    (async () => { await refresh(); setLoading(false); })();
    timerRef.current = setInterval(refresh, 2000);
    return () => clearInterval(timerRef.current);
  }, []);

  async function onFiles(fileList) {
    if (readOnly || !fileList?.length) return;
    setUploading(true);
    try {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("upload", f);
        await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${fileList.length} file(s)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  }

  async function cancelJob(jobId) {
    setCancelling((s) => ({ ...s, [jobId]: true }));
    try {
      const { data } = await api.post(`/geometry-jobs/${jobId}/cancel`);
      if (data.already_terminal) {
        toast.info(`Job already ${data.state} — nothing to cancel`);
      } else {
        toast.success("Cancellation requested");
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling((s) => { const n = { ...s }; delete n[jobId]; return n; });
    }
  }

  return (
    <div className="p-8 max-w-6xl">
      <h1 className="font-display text-3xl font-bold mb-2">Geometry jobs</h1>
      

      <label
        data-testid="file-upload-dropzone"
        className="block cursor-pointer border-2 border-dashed border-slate-300 hover:border-[var(--sq-accent)] rounded-lg p-8 text-center bg-white mb-6"
      >
        <UploadCloud className="w-8 h-8 mx-auto text-slate-400" />
        <div className="mt-2 text-sm text-slate-700">
          {readOnly ? "Read-only access — uploads are unavailable" : uploading ? "Uploading…" : "Select a DXF or a single-part STEP / STP file"}
        </div>
        
        <input
          data-testid="file-upload-input"
          type="file"
          multiple
          disabled={readOnly || uploading}
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
        />
      </label>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table data-testid="geometry-jobs-list" className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <Th>File</Th>
              <Th>State</Th>
              <Th className="text-right">Progress</Th>
              <Th>Executor</Th>
              <Th>Created</Th>
              <Th>Result</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500 font-mono text-xs">loading…</td></tr>}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">No jobs yet — upload a file above.</td></tr>
            )}
            {jobs.map((j) => {
              const f = files.find((x) => x.id === j.file_id);
              const isTerminal = TERMINAL.has(j.state);
              const isCancellationRequested = j.cancellation_requested;
              const busy = cancelling[j.id];
              return (
                <tr key={j.id} data-testid={`job-row-${j.id}`} className="border-b border-slate-100 align-top">
                  <td className="px-4 py-2.5 font-mono text-xs">{f?.filename || j.file_id.slice(0, 8)}</td>
                  <td className="px-4 py-2.5"><GeometryStatePill state={j.state} /></td>
                  <td className="px-4 py-2.5 numeric text-right">{j.progress}%</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-slate-500">{j.executor || "stub"}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 numeric">{fmtDate(j.created_at)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-700 max-w-md">
                    {j.result?.reason || j.error || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {!isTerminal && !isCancellationRequested && (
                      <button
                        data-testid={`job-cancel-${j.id}`}
                        onClick={() => cancelJob(j.id)}
                        disabled={busy || readOnly}
                        className="inline-flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900 disabled:opacity-40"
                        title="Cancel this job"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        {busy ? "Cancelling…" : "Cancel"}
                      </button>
                    )}
                    {isCancellationRequested && !isTerminal && (
                      <span data-testid={`job-cancel-pending-${j.id}`} className="text-[11px] text-slate-500 text-amber-700">
                        Cancelling…
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Th({ children, className = "" }) { return <th className={`px-4 py-2.5 text-[11px] text-slate-500 text-slate-500 font-medium ${className}`}>{children}</th>; }
